import React, { useState, useEffect } from 'react';

import Pricing from '../components/Pricing';
import PaymentMethodModal from '../components/PaymentMethodModal';
import { useTranslation } from '../hooks/useTranslation';
import DiamondVPNService from '../services/api';
import { useNavigate } from 'react-router-dom';
import "../styles/profile.css";
import SEO from '../components/SEO';

const Profile = () => {
  const navigate = useNavigate();
  const telegramBotUrl = 'https://t.me/diamondvpn_shopbot';
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [visiblePaymentsCount, setVisiblePaymentsCount] = useState(3);
  const [referralLink, setReferralLink] = useState('');
  const [referralStats, setReferralStats] = useState({ total_referrals: 0, active_referrals: 0 });
  const [visibleReferralsCount, setVisibleReferralsCount] = useState(5);
  const [copied, setCopied] = useState(false);
  const [subCopied, setSubCopied] = useState('');
  const [qrOpen, setQrOpen] = useState(false);
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [tgLoginOpen, setTgLoginOpen] = useState(false);
  const [tgStep, setTgStep] = useState(1);
  const [tgId, setTgId] = useState('');
  const [tgCode, setTgCode] = useState('');
  const [tgError, setTgError] = useState('');
  const [tgLoading, setTgLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const isRu = (i18n && typeof i18n.language === 'string' ? i18n.language : 'ru').startsWith('ru');
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetMethod, setResetMethod] = useState('crypto'); // 'yookassa' | 'crypto'
  const [resetPaying, setResetPaying] = useState(false);
  // Admin-controlled enabled payment methods (defaults to all on error / before load).
  const [enabledMethods, setEnabledMethods] = useState(['yookassa', 'platega', 'tribute', 'crypto']);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [visibleDevicesCount, setVisibleDevicesCount] = useState(5);
  const [urlRecreating, setUrlRecreating] = useState(false);
  const [recreateModalOpen, setRecreateModalOpen] = useState(false);
  const [trafficRange, setTrafficRange] = useState('week');
  const [trafficStats, setTrafficStats] = useState(null);
  const [trafficStatsLoading, setTrafficStatsLoading] = useState(false);
  const [trafficTooltip, setTrafficTooltip] = useState(null);
  const [recurringSubscription, setRecurringSubscription] = useState(null);
  const [recurringPlans, setRecurringPlans] = useState([]);
  const [recurringPlanId, setRecurringPlanId] = useState('');
  const [recurringLoading, setRecurringLoading] = useState(false);

  useEffect(() => {
    checkAuthentication();
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      const res = await DiamondVPNService.getPaymentMethods();
      const map = res?.methods;
      if (map && typeof map === 'object') {
        const enabled = Object.keys(map).filter((k) => map[k]);
        if (enabled.length) setEnabledMethods(enabled);
      }
    } catch (e) {
      // keep all methods enabled (current behavior)
    }
  };

  useEffect(() => {
    const subUuid = subscription?.uuid || subscription?.vless_uuid || subscription?.vlessUuid;
    if (isAuthenticated && subUuid) {
      loadTrafficStats(trafficRange);
    } else {
      setTrafficStats(null);
    }
  }, [isAuthenticated, subscription?.uuid, subscription?.vless_uuid, subscription?.vlessUuid, trafficRange]);

   useEffect(() => {
    if (user && user.id) {
      const referralLink = `${DiamondVPNService.baseURL}/?ref=${user.id}`;
      setReferralLink(referralLink);
    }
  }, [user]);
  
  const checkAuthentication = async () => {
    try {
      const authStatus = await DiamondVPNService.checkAuth();
      setIsAuthenticated(authStatus);
      
      if (authStatus) {
        await loadUserData();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setLoading(false);
    }
  };

  const loadUserData = async () => {
    try {
      const userData = await DiamondVPNService.getUserData();
      setUser(userData.user);
      setSubscription(userData.remnawave_user);
      setPayments(userData.payments || []);
      setPricing(userData.pricing || null);
      if (userData.platega_recurring_available) {
        const [recurringData, plansData] = await Promise.all([
          DiamondVPNService.getPlategaRecurringSubscription(),
          DiamondVPNService.getSubscriptionPlans(),
        ]);
        setRecurringSubscription(recurringData?.subscription || null);
        const monthlyPlans = (plansData?.plans || []).filter((plan) => Number(plan.days) === 30 && plan.platega_available);
        setRecurringPlans(monthlyPlans);
        if (monthlyPlans.length) setRecurringPlanId(String(monthlyPlans[0].id));
      }
      if (userData.user.referral_stats) {
        setReferralStats(userData.user.referral_stats);
      }
      if (userData.remnawave_user) {
        loadDevices();
      } else {
        setDevices([]);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };


  const copyToClipboard = async () => {
    try {
      if (!referralLink) return;
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const startRecurringSubscription = async () => {
    if (!recurringPlanId || recurringLoading) return;
    try {
      setRecurringLoading(true);
      const result = await DiamondVPNService.createPlategaRecurringSubscription(Number(recurringPlanId));
      window.location.assign(result.redirect_url);
    } catch (error) {
      alert(error.message || (isRu ? '\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u043f\u043e\u0434\u043a\u043b\u044e\u0447\u0438\u0442\u044c \u0430\u0432\u0442\u043e\u0441\u043f\u0438\u0441\u0430\u043d\u0438\u0435' : 'Unable to enable auto-renewal'));
    } finally {
      setRecurringLoading(false);
    }
  };

  const cancelRecurringSubscription = async () => {
    if (recurringLoading || !window.confirm(isRu ? '\u041e\u0442\u043a\u043b\u044e\u0447\u0438\u0442\u044c \u0431\u0443\u0434\u0443\u0449\u0438\u0435 \u0430\u0432\u0442\u043e\u0441\u043f\u0438\u0441\u0430\u043d\u0438\u044f?' : 'Cancel future automatic charges?')) return;
    try {
      setRecurringLoading(true);
      await DiamondVPNService.cancelPlategaRecurringSubscription();
      setRecurringSubscription((current) => current ? { ...current, status: 'CANCELLED', next_charge_at: null } : null);
    } catch (error) {
      alert(error.message || (isRu ? '\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u043e\u0442\u043a\u043b\u044e\u0447\u0438\u0442\u044c \u0430\u0432\u0442\u043e\u0441\u043f\u0438\u0441\u0430\u043d\u0438\u0435' : 'Unable to cancel auto-renewal'));
    } finally {
      setRecurringLoading(false);
    }
  };

  const requestTrial = async () => {
    try {
      const result = await DiamondVPNService.requestTrial();
      if (result.success) {
        await loadUserData();
        setTrialModalOpen(true);
      }
    } catch (error) {
      console.error('Error activating trial:', error);
      alert(error.message || t('profile.trial.activation_error'));
    }
  };

  const copySubscription = async () => {
    try {
      const url = subscription?.subscription_url;
      if (!url) return;
      await navigator.clipboard.writeText(url);
      setSubCopied(url);
      setTimeout(() => setSubCopied(''), 2000);
    } catch (e) {
      console.error('Failed to copy subscription url:', e);
    }
  };

  // Close QR modal with ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setQrOpen(false);
    };
    if (qrOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [qrOpen]);

  // Close trial modal with ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setTrialModalOpen(false);
    };
    if (trialModalOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [trialModalOpen]);

  const handleLogin = () => {
    DiamondVPNService.login();
  };

  const loadDevices = async () => {
    try {
      setDevicesLoading(true);
      const result = await DiamondVPNService.getSubscriptionDevices();
      setDevices(Array.isArray(result?.devices) ? result.devices : []);
      setVisibleDevicesCount(5);
    } catch (e) {
      console.warn('Failed to load devices:', e);
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };

  const loadTrafficStats = async (range) => {
    try {
      setTrafficStatsLoading(true);
      const result = await DiamondVPNService.getTrafficStats(range);
      setTrafficStats(result?.success ? result : null);
    } catch (e) {
      console.warn('Failed to load traffic stats:', e);
      setTrafficStats(null);
    } finally {
      setTrafficStatsLoading(false);
    }
  };

  const recreateSubscriptionUrl = async () => {
    if (urlRecreating) return;

    try {
      setUrlRecreating(true);
      const result = await DiamondVPNService.recreateSubscriptionUrl();
      if (!result?.success) {
        throw new Error(isRu ? 'Не удалось пересоздать URL' : 'Failed to recreate URL');
      }

      const remUser = result.remnawave_user || {};
      const nextUrl = result.subscription_url || remUser.subscription_url || remUser.subscriptionUrl;
      setSubscription(prev => ({
        ...(prev || {}),
        ...remUser,
        subscription_url: nextUrl || prev?.subscription_url,
      }));
      if (nextUrl) {
        await navigator.clipboard.writeText(nextUrl).catch(() => {});
        setSubCopied(nextUrl);
        setTimeout(() => setSubCopied(''), 2000);
      }
      await loadDevices();
      setRecreateModalOpen(false);
    } catch (e) {
      const msg = (e && e.message) ? e.message : String(e || '');
      alert(msg || (isRu ? 'Ошибка пересоздания URL' : 'URL recreate error'));
    } finally {
      setUrlRecreating(false);
    }
  };

  const openTgLogin = () => {
    setTgError('');
    setTgId('');
    setTgCode('');
    setTgStep(1);
    setTgLoginOpen(true);
  };

  const requestTgCode = async () => {
    try {
      setTgLoading(true);
      setTgError('');
      await DiamondVPNService.telegramLoginRequest(tgId);
      setTgStep(2);
    } catch (e) {
        const msg = (e && e.message) ? e.message : String(e || '');
        if (msg === 'userNotFound') {
          setTgError(isRu
          ? 'Пользователь не найден. Ваш Telegram ID можно посмотреть в боте по кнопке «Telegram ID (для входа)».'
          : 'User not found. You can find your Telegram ID in the bot using “Telegram ID (for login)”.'
        );
      } else {
        setTgError(msg || (isRu ? 'Не удалось запросить код' : 'Failed to request code'));
      }
    } finally {
      setTgLoading(false);
    }
  };

  const verifyTgCode = async () => {
    try {
      setTgLoading(true);
      setTgError('');
      await DiamondVPNService.telegramLoginVerify(tgId, tgCode);
      // refresh session state
      if (typeof window !== 'undefined' && window.location) {
        window.location.reload();
      }
    } catch (e) {
      const msg = (e && e.message) ? e.message : String(e || '');
      if (msg === 'codeInvalid' || msg === 'codeExpired') {
        setTgError(isRu ? 'Неверный или просроченный код' : 'Invalid or expired code');
      } else {
        setTgError(msg || (isRu ? 'Неверный код' : 'Invalid code'));
      }
    } finally {
      setTgLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await DiamondVPNService.logout();
      setUser(null);
      setSubscription(null);
      setPayments([]);
      setIsAuthenticated(false);
      setReferralLink('');
      setReferralStats({ total_referrals: 0, active_referrals: 0 });
      // Обновляем страницу, чтобы UI/сессия полностью синхронизировались
      if (typeof window !== 'undefined' && window.location) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const locale = 'ru-RU';
    return new Date(dateString).toLocaleDateString(locale);
  };

  const formatTraffic = (bytes) => {
    if (!bytes) return '0';
    const gb = bytes / (1024 * 1024 * 1024);
    return gb.toFixed(1);
  };

  const formatBytes = (bytes) => {
    const value = Number(bytes || 0);
    if (!Number.isFinite(value) || value <= 0) return '0 Б';
    const units = isRu ? ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'] : ['B', 'KB', 'MB', 'GB', 'TB'];
    const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
    const amount = value / Math.pow(1024, index);
    const text = amount >= 10 || index === 0 ? Math.round(amount).toString() : amount.toFixed(1);
    return `${text} ${units[index]}`;
  };

  const parseApiDate = (dateString) => {
    if (!dateString) return null;

    const raw = String(dateString).trim();
    const directDate = new Date(raw);
    if (!Number.isNaN(directDate.getTime())) {
      return directDate;
    }

    const normalized = raw
      .replace(' ', 'T')
      .replace(/(\.\d{3})\d+/, '$1');
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = parseApiDate(dateString);
      if (!date) return '';
      return date.toLocaleString(isRu ? 'ru-RU' : 'en-US', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return '';
    }
  };

  const formatTrafficDay = (dateString, index) => {
    if (!dateString) return isRu ? `День ${index + 1}` : `Day ${index + 1}`;

    const normalized = String(dateString).includes('T') ? dateString : `${dateString}T00:00:00`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return String(dateString);

    return date.toLocaleDateString(isRu ? 'ru-RU' : 'en-US', {
      day: 'numeric',
      month: 'short',
    });
  };

  const getOnlineAt = () => (
    subscription?.online_at
    || subscription?.onlineAt
    || subscription?.user_traffic?.online_at
    || subscription?.user_traffic?.onlineAt
    || subscription?.userTraffic?.online_at
    || subscription?.userTraffic?.onlineAt
    || subscription?.last_online_at
    || subscription?.lastOnlineAt
  );

  const getConnectionStatus = () => {
    const onlineAt = getOnlineAt();
    if (!onlineAt) {
      return { state: 'unknown', text: t('profile.connection_unknown') };
    }

    const onlineDate = parseApiDate(onlineAt);
    if (!onlineDate) {
      return { state: 'unknown', text: t('profile.connection_unknown') };
    }

    const isOnlineNow = (Date.now() - onlineDate.getTime()) <= 5 * 60 * 1000;
    return isOnlineNow
      ? { state: 'online', text: t('profile.connection_online') }
      : { state: 'offline', text: t('profile.connection_last_seen', { time: formatDateTime(onlineAt) }) };
  };

  const buildChartPath = (values, width = 640, height = 180) => {
    const rawData = Array.isArray(values) ? values.map(v => Math.max(0, Number(v || 0))) : [];
    const data = rawData.length === 1 ? [rawData[0], rawData[0]] : rawData;
    if (!data.length) return { line: '', area: '', points: [] };

    const max = Math.max(...data, 1);
    const padX = 18;
    const padY = 18;
    const innerW = width - padX * 2;
    const innerH = height - padY * 2;
    const points = data.map((value, index) => {
      const x = data.length === 1 ? width / 2 : padX + (index / (data.length - 1)) * innerW;
      const y = padY + innerH - (value / max) * innerH;
      return {
        x,
        y,
        value,
        sourceIndex: rawData.length === 1 ? 0 : index,
      };
    });

    const line = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ');
    const first = points[0];
    const last = points[points.length - 1];
    const area = `${line} L ${last.x.toFixed(1)} ${height - padY} L ${first.x.toFixed(1)} ${height - padY} Z`;

    return { line, area, points };
  };

  const showMorePayments = () => {
    setVisiblePaymentsCount(prevCount => prevCount + 3);
  };

  const showLessPayments = () => {
    setVisiblePaymentsCount(3);
  };

  const getSubscriptionStatus = (sub) => {
    if (!sub) return 'inactive';
    const exp = sub.expire_at ? new Date(sub.expire_at) : null;
    const now = new Date();
    // Consider subscription active if it hasn't expired yet,
    // regardless of weekly traffic exhaustion.
    if (exp && exp > now) return 'active';
    // Fallback to status if expire_at is missing
    const st = (sub.status || '').toString().trim().toUpperCase();
    if (st === 'ACTIVE') return 'active';
    return 'inactive';
  };

  const isSubscriptionActive = subscription && getSubscriptionStatus(subscription) === 'active';

  const visiblePayments = payments.slice(0, visiblePaymentsCount);
  const hasMorePayments = payments.length > visiblePaymentsCount;
  const hasManyPayments = payments.length > 3;
  const referrals = Array.isArray(referralStats.referrals) ? referralStats.referrals : [];
  const visibleReferrals = referrals.slice(0, visibleReferralsCount);
  const hasMoreReferrals = referrals.length > visibleReferralsCount;
  const hasManyReferrals = referrals.length > 5;
  const visibleDevices = devices.slice(0, visibleDevicesCount);
  const hasMoreDevices = devices.length > visibleDevicesCount;
  const hasManyDevices = devices.length > 5;

  const getDeviceValue = (device, snakeKey, camelKey) => device?.[snakeKey] || device?.[camelKey] || '';
  const getDeviceTitle = (device) => (
    getDeviceValue(device, 'device_model', 'deviceModel')
    || getDeviceValue(device, 'platform', 'platform')
    || (isRu ? 'Устройство' : 'Device')
  );
  const getDeviceMeta = (device) => {
    const parts = [
      getDeviceValue(device, 'platform', 'platform'),
      getDeviceValue(device, 'os_version', 'osVersion'),
    ].filter(Boolean);
    return parts.join(' · ');
  };

  const usedTraffic = subscription?.user_traffic?.used_traffic_bytes || 0;
  const totalTraffic = subscription?.traffic_limit_bytes || 0;
  const usedPercent = totalTraffic > 0 ? Math.min(100, Math.round((usedTraffic / totalTraffic) * 100)) : 0;
  const trafficExhausted = totalTraffic > 0 && usedTraffic >= totalTraffic;
  const pricePerGbPerMonth = Number(pricing?.price_per_gb_per_month || 0) || 0;
  const limitGbForReset = totalTraffic > 0 ? Math.ceil((Number(totalTraffic) / (1024 * 1024 * 1024)) * 100) / 100 : 0;
  const resetTrafficPrice = Math.round((limitGbForReset * pricePerGbPerMonth) * 100) / 100;
  const resetAllowedMethods = ((user?.region === 'ru') ? ['yookassa', 'platega', 'crypto'] : ['crypto'])
    .filter((m) => enabledMethods.includes(m));
  const connectionStatus = getConnectionStatus();
  const trafficData = Array.isArray(trafficStats?.sparklineData) ? trafficStats.sparklineData : [];
  const trafficCategories = Array.isArray(trafficStats?.categories) ? trafficStats.categories : [];
  const chart = buildChartPath(trafficData);
  const trafficTotal = Number(trafficStats?.total || 0);
  const topNodes = Array.isArray(trafficStats?.topNodes) ? trafficStats.topNodes : [];
  const maxNodeTraffic = Math.max(...topNodes.map(node => Number(node?.total || 0)), 1);
  const rangeTabs = [
    ['day', t('profile.traffic_range_day')],
    ['week', t('profile.traffic_range_week')],
    ['month', t('profile.traffic_range_month')],
  ];

  const openResetTraffic = () => {
    if (!resetTrafficPrice || resetTrafficPrice <= 0) return;
    const allowed = ((user?.region === 'ru') ? ['yookassa', 'crypto'] : ['crypto'])
      .filter((m) => enabledMethods.includes(m));
    const preferred = (user?.region === 'ru') ? 'yookassa' : 'crypto';
    setResetMethod(allowed.includes(preferred) ? preferred : (allowed[0] || 'crypto'));
    setResetModalOpen(true);
  };

  const confirmResetTrafficPayment = async () => {
    if (resetPaying) return;
    setResetPaying(true);
    try {
      if (resetMethod === 'yookassa') {
        const res = await DiamondVPNService.createResetTrafficPayment();
        if (res?.success && res?.confirmation_url) {
          window.location.href = res.confirmation_url;
        } else {
          throw new Error(isRu ? 'Не удалось подготовить платёж' : 'Payment failed');
        }
      } else if (resetMethod === 'platega') {
        const res = await DiamondVPNService.createResetTrafficPlategaPayment();
        if (res?.success && res?.redirect_url) {
          window.location.href = res.redirect_url;
        } else {
          throw new Error(isRu ? 'Не удалось подготовить платёж' : 'Payment failed');
        }
      } else if (resetMethod === 'crypto') {
        const res = await DiamondVPNService.createResetTrafficCryptoPayment();
        if (res?.success && res?.redirect_url) {
          window.location.href = res.redirect_url;
        } else {
          throw new Error(isRu ? 'Не удалось подготовить платёж' : 'Payment failed');
        }
      }
    } catch (e) {
      const msg = (e && e.message) ? e.message : String(e || '');
      alert(msg || (isRu ? 'Ошибка оплаты' : 'Payment error'));
    } finally {
      setResetPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')} {t('profile.title')}...</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <SEO
        title="Профиль и подписка — Diamond VPN"
        description="Управление профилем и подпиской Diamond VPN: тарифы, история платежей, реферальная программа и настройки."
        ogImage="https://diamondvpn.online/preview.jpg"
      />

      <div className="profile-container">
        {isAuthenticated && user ? (
          <>
            <div className="profile-card">
              <div className="profile-header">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    className="profile-avatar" 
                    crossOrigin="anonymous"
                    alt="Avatar"
                  />
                ) : (
                  <div className="profile-avatar profile-avatar--placeholder" aria-label="Avatar">
                    <i className="fas fa-user"></i>
                  </div>
                )}
                <div className="profile-info">
                  <h1>{user.name || t('profile.title')}</h1>
                  <p>{user.email}{user.registration_country ? ` В· ${user.registration_country}` : ""}</p>
                </div>
              </div>

              {recurringSubscription && ['PENDING', 'ACTIVE', 'SUBSCRIPTION_ACTIVATED'].includes(String(recurringSubscription.status).toUpperCase()) ? (
                <div className="subscription-actions recurring-payment-action">
                  <span className="recurring-payment-status">
                    {String(recurringSubscription.status).toUpperCase() === 'PENDING'
                      ? (isRu ? '\u0410\u0432\u0442\u043e\u0441\u043f\u0438\u0441\u0430\u043d\u0438\u0435 \u043e\u0436\u0438\u0434\u0430\u0435\u0442 \u043f\u043e\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043d\u0438\u044f' : 'Auto-renewal is awaiting confirmation')
                      : (isRu
                        ? `\u0410\u0432\u0442\u043e\u0441\u043f\u0438\u0441\u0430\u043d\u0438\u0435 \u043f\u043e\u0434\u043a\u043b\u044e\u0447\u0435\u043d\u043e${recurringSubscription.next_charge_at ? `. \u0421\u043b\u0435\u0434\u0443\u044e\u0449\u0435\u0435 \u0441\u043f\u0438\u0441\u0430\u043d\u0438\u0435: ${formatDate(recurringSubscription.next_charge_at)}` : ''}`
                        : `Auto-renewal enabled${recurringSubscription.next_charge_at ? `. Next charge: ${formatDate(recurringSubscription.next_charge_at)}` : ''}`)}
                  </span>
                  <button className="btn btn-secondary" onClick={cancelRecurringSubscription} disabled={recurringLoading}>
                    {recurringLoading ? (isRu ? '\u041e\u0442\u043a\u043b\u044e\u0447\u0430\u0435\u043c...' : 'Cancelling...') : (isRu ? '\u041e\u0442\u043a\u043b\u044e\u0447\u0438\u0442\u044c' : 'Cancel')}
                  </button>
                </div>
              ) : recurringPlans.length > 0 ? (
                <div className="subscription-actions recurring-payment-action">
                  <button className="btn btn-primary" onClick={startRecurringSubscription} disabled={recurringLoading}>
                    {recurringLoading ? (isRu ? '\u041e\u0442\u043a\u0440\u044b\u0432\u0430\u0435\u043c...' : 'Opening...') : (isRu ? '\u041f\u043e\u0434\u043a\u043b\u044e\u0447\u0438\u0442\u044c \u0430\u0432\u0442\u043e\u0441\u043f\u0438\u0441\u0430\u043d\u0438\u0435' : 'Enable auto-renewal')}
                  </button>
                </div>
              ) : null}

              <div className="subscription-status">
                <h3>{t('profile.subscription')}</h3>
                {isSubscriptionActive ? (
                  <>
                    <p className="status-active">
                      {t('profile.active', { date: formatDate(subscription.expire_at) })}
                    </p>
                    <div className={`connection-status ${connectionStatus.state}`}>
                      <span className="connection-dot" />
                      <span>{connectionStatus.text}</span>
                    </div>
                    {trafficExhausted && (
                      <p className="status-warning" role="status">
                        {isRu
                          ? `Достигнут недельный лимит трафика. Подписка активна до ${formatDate(subscription.expire_at)}.`
                          : `Weekly traffic limit reached. Subscription remains active until ${formatDate(subscription.expire_at)}.`}
                      </p>
                    )}
                    <div className="traffic-wrapper">
                      <div className="traffic-header">
                        <span className="traffic-amount">
                          {t('profile.traffic_weekly')}
                        </span>
                        <span className="traffic-subtext">
                          {t('profile.traffic_spent', { used: formatTraffic(usedTraffic), total: formatTraffic(totalTraffic) })}
                        </span>
                        <span className="traffic-percent">{usedPercent}%</span>
                      </div>
                      <div className="traffic-progress" aria-label="Traffic usage">
                        <div
                          className="traffic-progress__bar"
                          style={{ width: `${usedPercent}%` }}
                          role="progressbar"
                          aria-valuenow={usedPercent}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                      <div className="traffic-note">
                        {t('profile.traffic_reset_note')}
                      </div>
                      <div className="traffic-note">
                        {t('profile.traffic_renew_note')}
                      </div>
                      {resetTrafficPrice > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
                          <button className="btn btn-primary" onClick={openResetTraffic}>
                            {isRu ? 'Сбросить трафик' : 'Reset traffic'} ({resetTrafficPrice} {user?.currency || ''})
                          </button>
                        </div>
                      )}
                    </div>
                    {subscription.subscription_url && (<>
                      <div className="subscription-url-row">
                        <div
                          className={`referral-clickable ${subCopied ? 'copied' : ''}`}
                          onClick={copySubscription}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') copySubscription(); }}
                          title={subCopied ? t("profile.referral.copied") : t("profile.referral.copy")}
                        >
                          <i className="fas fa-link" />
                          <span className="referral-text">{subscription.subscription_url}</span>
                          <span className="referral-hint">{subCopied ? t("profile.referral.copied")  : t("profile.referral.copy")}</span>
                        </div>
                        <button
                          className="qr-icon-button"
                          onClick={() => setQrOpen(true)}
                          aria-label={t("profile.show_qr")}
                          title={t("profile.show_qr")}
                        >
                          <i className="fas fa-qrcode" />
                        </button>
                      </div>
                      <div>
                        <div className="subscription-actions">
                          <button
                            className="btn btn-primary"
                            onClick={() => navigate('/download')}
                          >
                            {t('profile.connect')}
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={() => setRecreateModalOpen(true)}
                            disabled={urlRecreating}
                          >
                            {urlRecreating
                              ? (isRu ? 'Пересоздаём...' : 'Recreating...')
                              : (isRu ? 'Пересоздать URL' : 'Recreate URL')}
                          </button>
                        </div>
                      </div>
                    <div className="traffic-chart-card">
                      <div className="traffic-chart-head">
                        <div>
                          <h4>{t('profile.traffic_chart_title')}</h4>
                          <p>{t('profile.traffic_chart_total')}: <b>{formatBytes(trafficTotal)}</b></p>
                        </div>
                        <div className="traffic-range-tabs" role="tablist" aria-label={t('profile.traffic_chart_title')}>
                          {rangeTabs.map(([key, label]) => (
                            <button
                              key={key}
                              type="button"
                              className={trafficRange === key ? 'active' : ''}
                              onClick={() => setTrafficRange(key)}
                              disabled={trafficStatsLoading}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {trafficStatsLoading ? (
                        <div className="traffic-chart-empty">
                          {isRu ? 'Загружаем статистику...' : 'Loading stats...'}
                        </div>
                      ) : chart.line && trafficTotal > 0 ? (
                        <>
                          <div className="traffic-chart-wrap">
                            <svg className="traffic-chart" viewBox="0 0 640 180" role="img" aria-label={t('profile.traffic_chart_title')}>
                              <defs>
                                <linearGradient id="trafficAreaGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#00c6ff" stopOpacity="0.28" />
                                  <stop offset="100%" stopColor="#0072ff" stopOpacity="0" />
                                </linearGradient>
                                <linearGradient id="trafficLineGradient" x1="0" y1="0" x2="1" y2="0">
                                  <stop offset="0%" stopColor="#0072ff" />
                                  <stop offset="100%" stopColor="#00c6ff" />
                                </linearGradient>
                              </defs>
                              <path className="traffic-chart-area" d={chart.area} />
                              <path className="traffic-chart-line" d={chart.line} />
                              {chart.points.map((point, index) => {
                                const sourceIndex = point.sourceIndex ?? index;
                                const label = formatTrafficDay(trafficCategories[sourceIndex], sourceIndex);
                                return (
                                  <circle
                                    key={index}
                                    className="traffic-chart-point"
                                    cx={point.x}
                                    cy={point.y}
                                    r="4"
                                    onMouseEnter={() => setTrafficTooltip({
                                      x: point.x,
                                      y: point.y,
                                      label,
                                      value: formatBytes(point.value),
                                    })}
                                    onMouseLeave={() => setTrafficTooltip(null)}
                                    onFocus={() => setTrafficTooltip({
                                      x: point.x,
                                      y: point.y,
                                      label,
                                      value: formatBytes(point.value),
                                    })}
                                    onBlur={() => setTrafficTooltip(null)}
                                  >
                                    <title>{label}: {formatBytes(point.value)}</title>
                                  </circle>
                                );
                              })}
                            </svg>
                            {trafficTooltip && (
                              <div
                                className="traffic-chart-tooltip"
                                style={{
                                  left: `${(trafficTooltip.x / 640) * 100}%`,
                                  top: `${(trafficTooltip.y / 180) * 100}%`,
                                }}
                              >
                                <span>{trafficTooltip.label}</span>
                                <b>{trafficTooltip.value}</b>
                              </div>
                            )}
                          </div>

                          {topNodes.length > 0 && (
                            <div className="traffic-top-nodes">
                              <h5>{t('profile.traffic_chart_top_nodes')}</h5>
                              {topNodes.map((node, index) => {
                                const nodeTotal = Number(node?.total || 0);
                                const width = Math.max(4, Math.round((nodeTotal / maxNodeTraffic) * 100));
                                return (
                                  <div className="traffic-node" key={node.uuid || node.name || index}>
                                    <div className="traffic-node-row">
                                      <span>{node.name || node.countryCode || (isRu ? 'Локация' : 'Location')}</span>
                                      <b>{formatBytes(nodeTotal)}</b>
                                    </div>
                                    <div className="traffic-node-bar">
                                      <span style={{ width: `${width}%` }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="traffic-chart-empty">
                          {t('profile.traffic_chart_empty')}
                        </div>
                      )}
                    </div>
                      <div className="devices-section">
                        <div className="devices-header">
                          <h4>{isRu ? 'Устройства' : 'Devices'}</h4>
                          <span>{devices.length}</span>
                        </div>

                        {devicesLoading ? (
                          <p className="devices-empty">{isRu ? 'Загружаем устройства...' : 'Loading devices...'}</p>
                        ) : devices.length > 0 ? (
                          <>
                            <div className="devices-list">
                              {visibleDevices.map((device, index) => {
                                const createdAt = getDeviceValue(device, 'created_at', 'createdAt');
                                return (
                                  <div key={device.hwid || index} className="device-item">
                                    <div>
                                      <strong>{getDeviceTitle(device)}</strong>
                                      {getDeviceMeta(device) && (
                                        <>
                                          <br />
                                          <small>{getDeviceMeta(device)}</small>
                                        </>
                                      )}
                                    </div>
                                    <div>
                                      {createdAt && <small>{formatDate(createdAt)}</small>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {hasManyDevices && (
                              <div className="payments-controls">
                                {hasMoreDevices ? (
                                  <button className="btn-show-more" onClick={() => setVisibleDevicesCount(prev => prev + 5)}>
                                    <span>{isRu ? 'Показать ещё' : 'Show more'}</span>
                                    <i className="fas fa-chevron-down"></i>
                                  </button>
                                ) : visibleDevicesCount > 5 ? (
                                  <button className="btn-show-less" onClick={() => setVisibleDevicesCount(5)}>
                                    <span>{isRu ? 'Скрыть' : 'Show less'}</span>
                                    <i className="fas fa-chevron-up"></i>
                                  </button>
                                ) : null}
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="devices-empty">
                            {isRu ? 'Подключённых устройств пока нет.' : 'No connected devices yet.'}
                          </p>
                        )}
                      </div>
                    </>)}
                  </>
                ) : (
                  <>
                    <p className="status-inactive">{t('profile.inactive')}</p>
                    {!user.trial_used && (
                      <div>
                        <button className="btn btn-success" onClick={requestTrial}>
                          {t('profile.trial.activate')}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="referral-section">
                <h3>{t('profile.referral.title')}</h3>
                <div className="referral-stats">
                  <div className="stat-item">
                    <span className="stat-number">{referralStats.total_referrals}</span>
                    <span className="stat-label">{t('profile.referral.total_referrals')}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{referralStats.active_referrals}</span>
                    <span className="stat-label">{t('profile.referral.active_referrals')}</span>
                  </div>
                </div>
                
                <div className="referral-link-container">
                  <p className="referral-description">
                    {t('profile.referral.description')}
                  </p>
                  <div
                    className={`referral-clickable ${copied ? 'copied' : ''}`}
                    onClick={copyToClipboard}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') copyToClipboard(); }}
                    title={copied ? t('profile.referral.copied') : t('profile.referral.copy')}
                  >
                    <i className="fas fa-link" />
                    <span className="referral-text">{referralLink}</span>
                    <span className="referral-hint">{copied ? t('profile.referral.copied') : t('profile.referral.copy')}</span>
                  </div>
                </div>

                <div className="referrals-list-section">
                  <div className="devices-header">
                    <h4>{isRu ? 'Приглашённые пользователи' : 'Invited users'}</h4>
                    <span>{referrals.length}</span>
                  </div>

                  {referrals.length > 0 ? (
                    <>
                      <div className="referrals-list">
                        {visibleReferrals.map((referral, index) => (
                          <div key={referral.id || index} className="referral-user-item">
                            <div>
                              <strong>{referral.email}</strong>
                            </div>
                            <div>
                              <span className={`referral-bonus ${referral.bonus_credited ? 'credited' : 'pending'}`}>
                                {referral.bonus_credited
                                  ? (isRu ? 'Начислено' : 'Credited')
                                  : (isRu ? 'Ожидает активации' : 'Pending')}
                              </span>
                              <br />
                              <small>{formatDate(referral.created_at)}</small>
                            </div>
                          </div>
                        ))}
                      </div>

                      {hasManyReferrals && (
                        <div className="payments-controls">
                          {hasMoreReferrals ? (
                            <button className="btn-show-more" onClick={() => setVisibleReferralsCount(prev => prev + 5)}>
                              <span>{isRu ? 'Показать ещё' : 'Show more'}</span>
                              <i className="fas fa-chevron-down"></i>
                            </button>
                          ) : visibleReferralsCount > 5 ? (
                            <button className="btn-show-less" onClick={() => setVisibleReferralsCount(5)}>
                              <span>{isRu ? 'Скрыть' : 'Show less'}</span>
                              <i className="fas fa-chevron-up"></i>
                            </button>
                          ) : null}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="devices-empty">
                      {isRu ? 'Пока никто не перешёл по вашей ссылке.' : 'No invited users yet.'}
                    </p>
                  )}
                </div>
              </div>

              <button className="btn btn-secondary" onClick={handleLogout}>
                {t('profile.logout')}
              </button>
            </div>

            <div className="profile-card">
              <h3>{t('profile.payments')}</h3>
              <div className="payments-list">
                {payments.length > 0 ? (
                  <>
                    {visiblePayments.map((payment, index) => (
                      <div key={payment.id || index} className="payment-item">
                        <div>
                          <strong>{payment.subscription?.name || t('profile.payment')}</strong>
                          <br />
                          <small>{formatDate(payment.created_at)}</small>
                        </div>
                        <div>
                          <strong>{payment.amount} {payment.subscription?.currency || payment.currency || 'RUB'}</strong>
                          <br />
                          <span className={`payment-status status-${payment.status}`}>
                            {payment.status === 'succeeded' && t('profile.payment_status.succeeded')}
                            {payment.status === 'pending' && t('profile.payment_status.pending')}
                            {payment.status === 'canceled' && t('profile.payment_status.canceled')}
                            {!['succeeded', 'pending', 'canceled'].includes(payment.status) && payment.status}
                          </span>
                        </div>
                      </div>
                    ))}
                    
                    {/* Р С™Р Р…Р С•Р С—Р С”Р С‘ РЎС“Р С—РЎР‚Р В°Р Р†Р В»Р ВµР Р…Р С‘РЎРЏ Р С•РЎвЂљР С•Р В±РЎР‚Р В°Р В¶Р ВµР Р…Р С‘Р ВµР С Р С—Р В»Р В°РЎвЂљР ВµР В¶Р ВµР в„– */}
                    {hasManyPayments && (
                      <div className="payments-controls">
                        {hasMorePayments ? (
                          <button className="btn-show-more" onClick={showMorePayments}>
                            <span>{t('profile.show_more')}</span>
                            <i className="fas fa-chevron-down"></i>
                          </button>
                        ) : visiblePaymentsCount > 3 ? (
                          <button className="btn-show-less" onClick={showLessPayments}>
                            <span>{t('profile.show_less')}</span>
                            <i className="fas fa-chevron-up"></i>
                          </button>
                        ) : null}
                      </div>
                    )}
                  </>
                ) : (
                  <p style={{ textAlign: 'center', color: 'var(--footer-color)' }}>
                    {t('profile.no_payments')}
                  </p>
                )}
              </div>
            </div>

            <div className="profile-card">
              <Pricing isAuthenticated={isAuthenticated} />
            </div>
          </>
        ) : (
          <div className="profile-card" style={{ textAlign: 'center' }}>
            <h2>{t('profile.login.title')}</h2>
            <p>{t('profile.login.description')}</p>
            <button
              className="btn btn-primary"
              onClick={handleLogin}
              style={{ marginTop: '20px', display: 'block', marginLeft: 'auto', marginRight: 'auto', width: '100%', maxWidth: 360 }}
            >
              <i className="fab fa-google"></i> {t('profile.login.google')}
            </button>
            <button
              className="btn btn-secondary"
              onClick={openTgLogin}
              style={{ marginTop: '12px', display: 'block', marginLeft: 'auto', marginRight: 'auto', width: '100%', maxWidth: 360 }}
            >
              <i className="fab fa-telegram"></i> {isRu ? 'Войти через Telegram' : 'Sign in with Telegram'}
            </button>
          </div>
        )}
      </div>
      {trialModalOpen && (
        <div
          className="qr-modal-overlay"
          onClick={() => setTrialModalOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="qr-body">
              <h3 style={{ textAlign: 'center' }}>
                {isRu ? 'Пробный период активирован' : 'Trial activated'}
              </h3>
              <p style={{ textAlign: 'center' }}>
                {isRu
                  ? 'Вы получили пробный период, подключитесь по кнопке ниже.'
                  : 'Your trial is activated. Connect using the button below.'}
              </p>
              {subscription?.subscription_url && (
                <button
                  className="btn btn-primary"
                  style={{ marginTop: 16 }}
                  onClick={() => {
                    navigate('/download');
                    setTrialModalOpen(false);
                  }}
                >
                  {t('profile.connect')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {qrOpen && subscription?.subscription_url && (
        <div className="qr-modal-overlay" onClick={() => setQrOpen(false)} role="dialog" aria-modal="true">
          <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
            <button className="qr-close" onClick={() => setQrOpen(false)} aria-label="Закрыть">×</button>
            <div className="qr-body">
              <img
                className="qr-image"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=512x512&data=${encodeURIComponent(subscription.subscription_url)}`}
                alt="QR код подписки"
                loading="lazy"
              />
              <div className="qr-url" title={subscription.subscription_url}>
                {subscription.subscription_url}
              </div>
            </div>
          </div>
        </div>
      )}
      {recreateModalOpen && (
        <div
          className="qr-modal-overlay"
          onClick={() => !urlRecreating && setRecreateModalOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="qr-modal recreate-url-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="qr-close"
              onClick={() => setRecreateModalOpen(false)}
              aria-label={isRu ? 'Закрыть' : 'Close'}
              disabled={urlRecreating}
            >
              ×
            </button>
            <div className="qr-body">
              <div className="recreate-url-icon">
                <i className="fas fa-link" />
              </div>
              <h3>{isRu ? 'Пересоздать URL подписки?' : 'Recreate subscription URL?'}</h3>
              <p>
                {isRu
                  ? 'Старый URL перестанет работать. Все устройства будут сброшены, их нужно будет подключить заново по новой ссылке.'
                  : 'The old URL will stop working. All devices will be reset and must be connected again using the new link.'}
              </p>
              <div className="recreate-url-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setRecreateModalOpen(false)}
                  disabled={urlRecreating}
                >
                  {isRu ? 'Отмена' : 'Cancel'}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={recreateSubscriptionUrl}
                  disabled={urlRecreating}
                >
                  {urlRecreating
                    ? (isRu ? 'Пересоздаём...' : 'Recreating...')
                    : (isRu ? 'Пересоздать' : 'Recreate')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {tgLoginOpen && (
        <div
          className="qr-modal-overlay"
          onClick={() => setTgLoginOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="qr-body">
              <h3 style={{ textAlign: 'center' }}>
                {isRu ? 'Вход через Telegram' : 'Telegram sign in'}
              </h3>
              {tgStep === 1 ? (
                <>
                  <p style={{ textAlign: 'center' }}>
                    {isRu ? 'Введите ваш Telegram ID. Его можно посмотреть в ' : 'Enter your Telegram ID. You can find it in the '}
                    <a href={telegramBotUrl} target="_blank" rel="noreferrer">
                      {isRu ? 'Telegram-боте' : 'Telegram bot'}
                    </a>
                    {isRu ? ' по кнопке «Telegram ID (для входа)».' : ' using “Telegram ID (for login)”.'}
                  </p>
                  <input
                    type="text"
                    value={tgId}
                    onChange={(e) => setTgId(e.target.value)}
                    placeholder={isRu ? 'Telegram ID' : 'Telegram ID'}
                    style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)' }}
                  />
                  {!!tgError && <p style={{ color: '#ffb3b3', marginTop: 10 }}>{tgError}</p>}
                  <button
                    className="btn btn-primary"
                    style={{ marginTop: 16, width: '100%' }}
                    onClick={requestTgCode}
                    disabled={tgLoading || !String(tgId || '').trim()}
                  >
                    {tgLoading ? (isRu ? 'Отправляем...' : 'Sending...') : (isRu ? 'Получить код' : 'Get code')}
                  </button>
                </>
              ) : (
                <>
                  <p style={{ textAlign: 'center' }}>
                    {isRu
                      ? 'Мы отправили код в Telegram бот. Введите его ниже.'
                      : 'We sent a code to your Telegram bot. Enter it below.'}
                  </p>
                  <input
                    type="text"
                    value={tgCode}
                    onChange={(e) => setTgCode(e.target.value)}
                    placeholder={isRu ? 'Код из Telegram' : 'Code from Telegram'}
                    style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)' }}
                  />
                  {!!tgError && <p style={{ color: '#ffb3b3', marginTop: 10 }}>{tgError}</p>}
                  <button
                    className="btn btn-primary"
                    style={{ marginTop: 16, width: '100%' }}
                    onClick={verifyTgCode}
                    disabled={tgLoading || !String(tgCode || '').trim()}
                  >
                    {tgLoading ? (isRu ? 'Проверяем...' : 'Verifying...') : (isRu ? 'Войти' : 'Sign in')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <PaymentMethodModal
        open={resetModalOpen}
        method={resetMethod}
        onChangeMethod={setResetMethod}
        onConfirm={confirmResetTrafficPayment}
        onCancel={() => setResetModalOpen(false)}
        isLoading={resetPaying}
        allowedMethods={resetAllowedMethods}
        enabledMethods={enabledMethods}
      />
    </div>
  );
};

export default Profile;

